import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, IconButton, Paper, Skeleton, Snackbar, Tooltip,
  Typography, alpha, useTheme,
} from '@mui/material';
import {
  Image as ImageIcon, Delete as DeleteIcon,
} from '@mui/icons-material';
import venueLogoService, { type VenueLogoMetadata } from '../../services/venueLogoService';
import { buildPublicVenueLogoUrl } from '../../utils/venueLogoUrl';

interface Props {
  venueId: string;
}

const ACCEPTED_MIME = 'image/png,image/jpeg,image/webp';
const MAX_BYTES = 2 * 1024 * 1024;

/**
 * Venue logo dashboard tile.
 *
 * Sized and shaped to match the StatCard tiles next to it on the venue
 * dashboard:
 *   - same Paper styling (radius, shadow, padding)
 *   - same flex basis + minWidth + maxWidth so it sits in the row at the
 *     same width as Wine Lists / Inventory Items / etc, and doesn't stretch
 *     to full width when it wraps to its own line
 *   - same three-row rhythm: uppercase label + icon → content → action link
 *
 * Image <img> uses the public binary URL (works without a bearer token);
 * upload / delete still go through the authenticated admin API.
 */
const VenueLogoPanel: React.FC<Props> = ({ venueId }) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [metadata, setMetadata] = useState<VenueLogoMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const refresh = () => {
    setLoading(true);
    setError('');
    venueLogoService.getMetadata(venueId)
      .then(setMetadata)
      .catch(() => setError('Could not load logo.'))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [venueId]);

  const handlePick = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.size > MAX_BYTES) {
      setError('File too large. Max 2 MB.');
      return;
    }

    setWorking(true);
    setError('');
    try {
      await venueLogoService.upload(venueId, file);
      refresh();
      setToast('Logo updated');
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Could not upload logo.');
    } finally {
      setWorking(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove the venue logo? Customer wine list pages will fall back to the venue name.')) return;
    setWorking(true);
    setError('');
    try {
      await venueLogoService.remove(venueId);
      setMetadata(null);
      setToast('Logo removed');
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Could not remove logo.');
    } finally {
      setWorking(false);
    }
  };

  const logoSrc = metadata?.logoUrl
    ? buildPublicVenueLogoUrl(venueId, metadata.uploadedAt ?? Date.now())
    : null;

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 2,
        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        // EXACT same flex sizing as StatCard so the tile sits at the same
        // width when it shares a row with the other dashboard tiles.
        flex: '1 1 200px',
        minWidth: 180,
        // The 6th tile may wrap to its own line; without a maxWidth a lone
        // flex-grow:1 child stretches to fill the row. Cap it slightly above
        // the typical StatCard width so it stays a tile-shape regardless.
        maxWidth: 260,
      }}
    >
      {/* Header — matches StatCard's "label + icon" row exactly. */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{
            color: theme.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Venue Logo
        </Typography>
        <Box sx={{ color: theme.palette.text.secondary, opacity: 0.7, display: 'flex' }}>
          <ImageIcon />
        </Box>
      </Box>

      {/* Body — replaces StatCard's "big number" with a logo thumbnail.
          Same vertical footprint as the value on adjacent cards (≈56px). */}
      <Box sx={{ height: 56, display: 'flex', alignItems: 'center' }}>
        {loading ? (
          <Skeleton variant="rounded" width={56} height={56} />
        ) : logoSrc ? (
          <Box
            component="img"
            src={logoSrc}
            alt="Venue logo"
            sx={{
              maxHeight: 56,
              maxWidth: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        ) : (
          <Tooltip title="Used on public wine lists" placement="top">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 1,
                border: `1px dashed ${alpha(theme.palette.divider, 0.6)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.text.disabled,
              }}
            >
              <ImageIcon fontSize="small" />
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Action row — matches StatCard's bottom action link.
          Remove icon is inline on the right so it doesn't add a fourth row. */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          size="small"
          onClick={handlePick}
          disabled={working || loading}
          sx={{
            textTransform: 'none',
            alignSelf: 'flex-start',
            color: theme.palette.primary.main,
            p: 0,
            minWidth: 0,
            fontWeight: 600,
            '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
          }}
        >
          {metadata ? 'Replace' : 'Upload'} →
        </Button>
        {metadata && !loading && (
          <Tooltip title="Remove logo">
            <IconButton
              size="small"
              color="error"
              onClick={handleRemove}
              disabled={working}
              aria-label="Remove venue logo"
              sx={{ p: 0.25 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mt: 1, borderRadius: 1, py: 0.25 }}>
          <Typography variant="caption">{error}</Typography>
        </Alert>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_MIME}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={1800}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
};

export default VenueLogoPanel;
